
import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, PlusCircle } from "lucide-react";
import { useTranslation } from "react-i18next";

interface CPVSuggestionsFromTextProps {
  searchText: string | null;
  existingSelections: string[];
  onCPVSelect: (cpv: string) => void;
}

interface CPVCode {
  CPV: number;
  Beskrivelse: string;
}

export const CPVSuggestionsFromText = ({
  searchText,
  existingSelections,
  onCPVSelect,
}: CPVSuggestionsFromTextProps) => {
  const [debouncedText, setDebouncedText] = useState<string | null>(searchText);
  const { t, i18n } = useTranslation("profile");
  
  // Set up debounced search to avoid too many queries
  useEffect(() => {
    const handler = setTimeout(() => {
      if (searchText) {
        setDebouncedText(searchText);
      }
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchText]);

  const { data: suggestions, isLoading } = useQuery({
    queryKey: ["cpv-text-suggestions", debouncedText],
    queryFn: async () => {
      if (!debouncedText || debouncedText.trim().length < 3) return [];

      // Split the search text by commas to get individual keywords
      const keywords = debouncedText
        .split(',')
        .map(k => k.trim())
        .filter(k => k.length >= 3);
      
      if (keywords.length === 0) return [];

      // Use the first 5 keywords for searching
      const searchTerms = keywords.slice(0, 5);
      
      let allSuggestions: CPVCode[] = [];
      
      // Search for each keyword
      for (const term of searchTerms) {
        const { data, error } = await supabase
          .from("CPV Table")
          .select("CPV, Beskrivelse")
          .ilike("Beskrivelse", `%${term}%`)
          .limit(5);

        if (error) {
          console.error('Error fetching CPV suggestions:', error);
          continue;
        }

        // Add to our collective results
        if (data) {
          allSuggestions = [...allSuggestions, ...data];
        }
      }

      // Remove duplicates and filter out already selected CPVs
      const uniqueSuggestions = Array.from(
        new Map(allSuggestions.map(item => [item.CPV, item]))
      ).map(([_, item]) => item);

      return uniqueSuggestions
        .filter(cpv => !existingSelections.includes(cpv.CPV.toString()))
        .slice(0, 5); // Limit to 5 suggestions
    },
    enabled: !!debouncedText && debouncedText.trim().length >= 3,
  });

  if (!debouncedText || debouncedText.trim().length < 3 || !suggestions?.length) {
    return null;
  }

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 mt-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        {i18n.language === "da" ? "Finder relevante CPV-koder..." : "Finding relevant CPV codes..."}
      </div>
    );
  }

  return (
    <div className="mt-4 space-y-2">
      <p className="text-sm text-muted-foreground">
        {i18n.language === "da" ? "Branchekoder der matcher dine søgeord:" : "Industry codes matching your keywords:"}
      </p>
      <div className="flex flex-wrap gap-2">
        {suggestions.map((cpv) => (
          <Badge
            key={cpv.CPV}
            variant="outline"
            className="px-3 py-1.5 cursor-pointer hover:bg-primary/10 group flex items-center gap-1"
            onClick={() => onCPVSelect(cpv.CPV.toString())}
          >
            <PlusCircle className="h-3.5 w-3.5 mr-1 text-primary" />
            {cpv.Beskrivelse}
          </Badge>
        ))}
      </div>
    </div>
  );
};
